/**
 * Scenario Planning & What-If Analysis Utilities
 */

import { Scenario, ExtendedArchiMateDiagram } from './extended-types';
import { uuidv7 } from 'uuidv7';

export function createScenario(
  name: string,
  description: string,
  diagramId: string,
  baselineDiagramId?: string
): Scenario {
  return {
    id: uuidv7(),
    name,
    description,
    diagramId,
    baselineDiagramId,
    scores: {
      cost: 5,
      complexity: 5,
      risk: 5,
      timeToMarket: 5,
      scalability: 5,
      maintainability: 5,
    },
    pros: [],
    cons: [],
    assumptions: [],
    totalScore: 30, // Sum of all scores
    createdAt: Date.now(),
  };
}

export function calculateTotalScore(scenario: Scenario): number {
  return Object.values(scenario.scores).reduce((sum, score) => sum + score, 0);
}

export function updateScenarioScore(
  scenario: Scenario,
  dimension: keyof Scenario['scores'],
  score: number
): Scenario {
  const updated = {
    ...scenario,
    scores: {
      ...scenario.scores,
      [dimension]: Math.max(1, Math.min(10, score)), // Clamp between 1-10
    },
  };
  updated.totalScore = calculateTotalScore(updated);
  return updated;
}

export function compareScenarios(scenarios: Scenario[]): {
  winner: Scenario;
  comparison: Array<{
    dimension: string;
    winner: Scenario;
    scores: Array<{ scenario: Scenario; score: number }>;
  }>;
  recommendation: string;
} {
  if (scenarios.length === 0) {
    throw new Error('No scenarios to compare');
  }

  // Find overall winner
  const winner = scenarios.reduce((best, current) =>
    current.totalScore > best.totalScore ? current : best
  );

  // Compare each dimension
  const dimensions: Array<keyof Scenario['scores']> = [
    'cost',
    'complexity',
    'risk',
    'timeToMarket',
    'scalability',
    'maintainability',
  ];

  const comparison = dimensions.map((dimension) => {
    const scores = scenarios.map((scenario) => ({
      scenario,
      score: scenario.scores[dimension],
    }));

    const dimensionWinner = scores.reduce((best, current) =>
      current.score > best.score ? current : best
    ).scenario;

    return {
      dimension,
      winner: dimensionWinner,
      scores,
    };
  });

  // Generate recommendation
  let recommendation = `**Recommended: ${winner.name}** (Total Score: ${winner.totalScore}/60)\n\n`;
  recommendation += `**Strengths:**\n`;
  comparison.forEach(({ dimension, winner: dimWinner }) => {
    if (dimWinner.id === winner.id) {
      recommendation += `- Best ${dimension} (${winner.scores[dimension]}/10)\n`;
    }
  });

  recommendation += `\n**Consider:**\n`;
  scenarios
    .filter((s) => s.id !== winner.id)
    .forEach((scenario) => {
      const betterDimensions = comparison
        .filter(({ winner: dimWinner }) => dimWinner.id === scenario.id)
        .map(({ dimension }) => dimension);

      if (betterDimensions.length > 0) {
        recommendation += `- ${scenario.name} excels in: ${betterDimensions.join(', ')}\n`;
      }
    });

  return { winner, comparison, recommendation };
}

export function generateScenarioMatrix(scenarios: Scenario[]): string {
  let matrix = '| Dimension | ';
  matrix += scenarios.map((s) => s.name).join(' | ');
  matrix += ' |\n';

  matrix += '|-----------|';
  matrix += scenarios.map(() => '------').join('|');
  matrix += '|\n';

  const dimensions: Array<{ key: keyof Scenario['scores']; label: string }> = [
    { key: 'cost', label: 'Cost' },
    { key: 'complexity', label: 'Complexity' },
    { key: 'risk', label: 'Risk' },
    { key: 'timeToMarket', label: 'Time to Market' },
    { key: 'scalability', label: 'Scalability' },
    { key: 'maintainability', label: 'Maintainability' },
  ];

  dimensions.forEach(({ key, label }) => {
    matrix += `| ${label} | `;
    matrix += scenarios.map((s) => `${s.scores[key]}/10`).join(' | ');
    matrix += ' |\n';
  });

  matrix += '| **Total** | ';
  matrix += scenarios.map((s) => `**${s.totalScore}/60**`).join(' | ');
  matrix += ' |\n';

  return matrix;
}

export function generateScenarioReport(scenarios: Scenario[]): string {
  let report = `# Scenario Analysis\n\n`;
  report += `**Scenarios Evaluated:** ${scenarios.length}\n`;
  report += `**Date:** ${new Date().toISOString().split('T')[0]}\n\n`;

  report += `## Score Matrix\n\n`;
  report += generateScenarioMatrix(scenarios);

  report += `\n## Detailed Comparison\n\n`;
  const { winner, comparison, recommendation } = compareScenarios(scenarios);

  report += recommendation;

  report += `\n## Individual Scenarios\n\n`;
  scenarios.forEach((scenario) => {
    report += `### ${scenario.name} (${scenario.totalScore}/60)\n\n`;
    report += `${scenario.description}\n\n`;

    if (scenario.pros.length > 0) {
      report += `**Pros:**\n`;
      scenario.pros.forEach((pro) => {
        report += `- ${pro}\n`;
      });
      report += `\n`;
    }

    if (scenario.cons.length > 0) {
      report += `**Cons:**\n`;
      scenario.cons.forEach((con) => {
        report += `- ${con}\n`;
      });
      report += `\n`;
    }

    if (scenario.assumptions.length > 0) {
      report += `**Assumptions:**\n`;
      scenario.assumptions.forEach((assumption) => {
        report += `- ${assumption}\n`;
      });
      report += `\n`;
    }
  });

  return report;
}

export function rankScenarios(
  scenarios: Scenario[],
  weights?: Partial<Record<keyof Scenario['scores'], number>>
): Array<{ scenario: Scenario; weightedScore: number }> {
  // Default equal weights
  const defaultWeights = {
    cost: 1,
    complexity: 1,
    risk: 1,
    timeToMarket: 1,
    scalability: 1,
    maintainability: 1,
  };

  const actualWeights = { ...defaultWeights, ...weights };

  const ranked = scenarios.map((scenario) => {
    let weightedScore = 0;
    let totalWeight = 0;

    Object.entries(scenario.scores).forEach(([key, score]) => {
      const weight = actualWeights[key as keyof Scenario['scores']];
      weightedScore += score * weight;
      totalWeight += weight;
    });

    // Normalize to 0-100 scale
    const normalizedScore = (weightedScore / (totalWeight * 10)) * 100;

    return { scenario, weightedScore: normalizedScore };
  });

  return ranked.sort((a, b) => b.weightedScore - a.weightedScore);
}

export function scenarioSensitivityAnalysis(
  scenario: Scenario,
  dimension: keyof Scenario['scores'],
  range: { min: number; max: number }
): Array<{ value: number; totalScore: number }> {
  const results: Array<{ value: number; totalScore: number }> = [];

  for (let value = range.min; value <= range.max; value++) {
    const updated = updateScenarioScore(scenario, dimension, value);
    results.push({ value, totalScore: updated.totalScore });
  }

  return results;
}

export function cloneScenario(scenario: Scenario, newName: string): Scenario {
  return {
    ...scenario,
    id: uuidv7(),
    name: newName,
    createdAt: Date.now(),
  };
}

export function exportScenariosToCSV(scenarios: Scenario[]): string {
  let csv = 'Scenario,Cost,Complexity,Risk,Time to Market,Scalability,Maintainability,Total Score\n';

  scenarios.forEach((scenario) => {
    csv += `"${scenario.name}",`;
    csv += `${scenario.scores.cost},`;
    csv += `${scenario.scores.complexity},`;
    csv += `${scenario.scores.risk},`;
    csv += `${scenario.scores.timeToMarket},`;
    csv += `${scenario.scores.scalability},`;
    csv += `${scenario.scores.maintainability},`;
    csv += `${scenario.totalScore}\n`;
  });

  return csv;
}

// Alias for backward compatibility
export const exportScenarioReport = generateScenarioReport;
